/**
 * The small pill beside a name — `docs/proposal.md` board 4's three kinds of
 * visit, said at a glance: **Included** (in the brand tint, ticked),
 * **Optional** (the fixed blue, a plus), **Added** (an optional the customer
 * switched on — the brand tint again, because it is now part of the price),
 * **Choose one** (the fixed purple, a pencil) and **Recommended** (filled).
 *
 * The blue and the purple are tokens rather than the brand on purpose (§5.3):
 * "optional" has to mean the same thing on every company's proposal.
 */
import type { ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { TagCheck, TagPencil, TagPlus, TagStar } from './glyphs.js'

export type TagKind = 'included' | 'added' | 'optional' | 'choose' | 'recommended'

const LOOK: Record<TagKind, { className: string; glyph: ReactNode; words: string }> = {
  included: { className: 'bg-brand-t2 text-brand-tx', glyph: <TagCheck size={12} />, words: 'Included' },
  added: { className: 'bg-brand-t2 text-brand-tx', glyph: <TagCheck size={12} />, words: 'Added' },
  optional: {
    className: 'bg-tag-optional-bg text-tag-optional-fg', glyph: <TagPlus size={12} />, words: 'Optional',
  },
  choose: {
    className: 'bg-tag-choose-bg text-tag-choose-fg', glyph: <TagPencil size={12} />, words: 'Choose one',
  },
  recommended: {
    className: 'bg-brand text-brand-on', glyph: <TagStar size={12} />, words: 'Recommended',
  },
}

export function ProposalTag({ kind, className }: { kind: TagKind; className?: string }) {
  const look = LOOK[kind]
  return (
    <span className={cn('inline-flex h-6 shrink-0 items-center gap-[5px] whitespace-nowrap',
      'rounded-full pl-[7px] pr-[9px] text-[12px] font-bold', look.className, className)}>
      {look.glyph}
      {look.words}
    </span>
  )
}
